import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FiCheck, FiX, FiMinus, FiPlus, FiZap, FiAlertTriangle } from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import '../styles/PlanChangeDrawer.css';

const GET_PLANS = `
  query GetBillingPlans {
    getBillingPlans
  }
`;

const PREVIEW = `
  query PreviewMyBillingPlanChange($input: JSON!) {
    previewMyBillingPlanChange(input: $input)
  }
`;

const CHANGE_PLAN = `
  mutation ChangeMyBillingPlan($priceId: String!, $input: JSON) {
    changeMyBillingPlan(priceId: $priceId, input: $input)
  }
`;

const STEPS = ['Choose', 'Review', 'Confirm'];
const REASONS = [
  'Need more features',
  'Team is growing',
  'Reduce costs',
  'Change billing cycle',
  'Other',
];
const CYCLE_SHORT = { month: 'mo', year: 'yr' };

/** A plan includes every feature of the lower-positioned plans (same rule as the Plans tab). */
function featureSet(plans, plan, cycle) {
  if (!plan) return new Set();
  const own = (p) => [
    ...p.features,
    ...(p.isCustom ? p.compareFeatures.custom : p.compareFeatures[cycle] || []),
  ];
  const lower = plans.filter(
    (o) => !o.isCustom && (o.sortOrder ?? Infinity) < (plan.sortOrder ?? -Infinity),
  );
  return new Set([...lower.flatMap(own), ...own(plan)]);
}

/**
 * Change plan: Choose (plan, cycle, staff, when) → Review (what changes, price
 * breakdown) → Confirm (payment method). Applies straight away; no approval step.
 */
export default function PlanChangeDrawer({
  overview,
  staffUsed = 0,
  initialPriceId = '',
  formatMoney,
  formatDate,
  onClose,
  onChanged,
  onManageCards,
  onContactSales,
}) {
  const current = overview.plan;
  const [plans, setPlans] = useState(null);
  const [step, setStep] = useState(0);
  const [cycle, setCycle] = useState(current.interval === 'year' ? 'year' : 'month');
  const [planName, setPlanName] = useState('');
  const [staff, setStaff] = useState(0);
  const [effective, setEffective] = useState('immediately');
  const [reason, setReason] = useState(REASONS[0]);
  const [contactEmail, setContactEmail] = useState(overview.billingEmail || '');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    graphqlRequest(GET_PLANS)
      .then((res) => {
        const list = res?.getBillingPlans || [];
        setPlans(list);
        const start =
          list.find((p) => p.prices.some((pr) => pr.id === initialPriceId)) ||
          list.find((p) => p.prices.some((pr) => pr.id === current.priceId));
        const startPrice = start?.prices.find((pr) => pr.id === initialPriceId);
        if (startPrice) setCycle(startPrice.interval);
        setPlanName(start?.name || '');
      })
      .catch((e) => setError(e?.message || 'Failed to load plans.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const currentPlan = (plans || []).find((p) =>
    p.prices.some((pr) => pr.id === current.priceId),
  );
  const plan = (plans || []).find((p) => p.name === planName);
  const price = plan?.prices.find((pr) => pr.interval === cycle);
  const included = typeof plan?.totalStaff === 'number' ? plan.totalStaff : null;
  const currentStaffTotal =
    (typeof current.totalStaff === 'number' ? current.totalStaff : 0) +
    (current.extraStaff || 0);

  // Staff starts at what the company has (or uses), never below active staff.
  useEffect(() => {
    if (plans) setStaff(Math.max(staffUsed, currentStaffTotal, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans]);

  const savedMonths = useMemo(
    () =>
      Math.max(
        0,
        ...(plans || []).map((p) => {
          const m = p.prices.find((pr) => pr.interval === 'month');
          const y = p.prices.find((pr) => pr.interval === 'year');
          return m && y && m.amount > 0 ? Math.round((m.amount * 12 - y.amount) / m.amount) : 0;
        }),
      ),
    [plans],
  );

  const { gains, losses } = useMemo(() => {
    if (!plans || !plan) return { gains: [], losses: [] };
    const before = featureSet(plans, currentPlan, current.interval || 'month');
    const after = featureSet(plans, plan, cycle);
    return {
      gains: [...after].filter((f) => !before.has(f)),
      losses: [...before].filter((f) => !after.has(f)),
    };
  }, [plans, plan, currentPlan, cycle, current.interval]);

  const nextCycleDate =
    overview.status === 'trialing' && overview.trialEnd
      ? overview.trialEnd
      : overview.currentPeriodEnd;
  const unitLabel = price ? `${formatMoney(price.amount, price.currency, true)}/${CYCLE_SHORT[price.interval] || price.interval}` : '';
  const isUpgrade =
    price && current.amount
      ? (price.interval === 'year' ? price.amount / 12 : price.amount) >
        (current.interval === 'year' ? current.amount / 12 : current.amount)
      : true;

  const loadPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await graphqlRequest(PREVIEW, {
        input: { priceId: price.id, staff, effective },
      });
      setPreview(res?.previewMyBillingPlanChange || null);
      setStep(1);
    } catch (e) {
      setError(e?.message || 'Could not price this change.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await graphqlRequest(CHANGE_PLAN, {
        priceId: price.id,
        input: {
          staff,
          effective,
          prorationDate: preview?.prorationDate,
          reason,
          contactEmail,
        },
      });
      toast.success(
        effective === 'next_cycle'
          ? `${plan.name} starts ${formatDate(preview?.effectiveDate)}.`
          : `You're now on ${plan.name}.`,
      );
      onChanged(res?.changeMyBillingPlan);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not change plan.');
    } finally {
      setBusy(false);
    }
  };

  const currency = preview?.currency || price?.currency || current.currency;
  const dueToday = preview?.dueToday ?? 0;
  const needsCard = dueToday > 0 && !overview.paymentMethod;
  const footerPrice = preview?.plan
    ? `${formatMoney(preview.plan.recurringAmount, currency)}/${CYCLE_SHORT[preview.plan.interval] || preview.plan.interval}`
    : unitLabel;

  const renderChoose = () => (
    <>
      <p className="pcd-label">Select a new plan</p>
      <div className="pcd-plans">
        {(plans || []).map((p) => {
          const pr = p.prices.find((x) => x.interval === cycle);
          const disabled = !p.isCustom && !pr;
          return (
            <label key={p.name} className={`pcd-option ${planName === p.name ? 'on' : ''} ${disabled ? 'off' : ''}`}>
              <input
                type="radio"
                name="pcd-plan"
                checked={planName === p.name}
                disabled={disabled}
                onChange={() => setPlanName(p.name)}
              />
              <span>
                <strong>{p.name}</strong>
                <small>
                  {p.isCustom || !pr
                    ? 'Custom'
                    : `${formatMoney(pr.amount, pr.currency, true)}/${CYCLE_SHORT[pr.interval] || pr.interval}`}
                  {p === currentPlan ? ' · Current' : ''}
                </small>
              </span>
            </label>
          );
        })}
      </div>

      <div className="pcd-row">
        <div>
          <p className="pcd-label">Billing cycle</p>
          <div className="pcd-cycle">
            {['month', 'year'].map((c) => (
              <button key={c} type="button" className={cycle === c ? 'on' : ''} onClick={() => setCycle(c)}>
                {c === 'month' ? 'Monthly' : 'Annual'}
                {c === 'year' && savedMonths > 0 && <small>Save {savedMonths} months</small>}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="pcd-label">Staff count</p>
          <div className="pcd-stepper">
            <button type="button" aria-label="Fewer staff" disabled={staff <= Math.max(1, staffUsed)} onClick={() => setStaff((n) => n - 1)}>
              <FiMinus />
            </button>
            <input
              type="number"
              min={Math.max(1, staffUsed)}
              value={staff}
              onChange={(e) => setStaff(Math.max(Math.max(1, staffUsed), Number(e.target.value) || 0))}
            />
            <button type="button" aria-label="More staff" onClick={() => setStaff((n) => n + 1)}>
              <FiPlus />
            </button>
          </div>
        </div>
      </div>
      {plan && !plan.isCustom && (
        <p className="pcd-hint">
          {included === null
            ? 'Unlimited staff on this plan.'
            : `${included} staff included${plan.pricePerStaff ? ` · extra staff ${formatMoney(plan.pricePerStaff, currency)}/user/month` : ''}${staff > included && plan.pricePerStaff ? ` · ${staff - included} extra` : ''}.`}
          {staffUsed ? ` You have ${staffUsed} active staff.` : ''}
        </p>
      )}

      <p className="pcd-label">Effective date</p>
      {[
        ['immediately', 'Immediately', 'Best for upgrades. Proration applies.'],
        ['next_cycle', 'Start of next billing cycle', formatDate(nextCycleDate)],
      ].map(([key, title, text]) => (
        <label key={key} className={`pcd-option pcd-option--wide ${effective === key ? 'on' : ''}`}>
          <input type="radio" name="pcd-effective" checked={effective === key} onChange={() => setEffective(key)} />
          <span>
            <strong>{title}</strong>
            <small>{text}</small>
          </span>
        </label>
      ))}

      <div className="pcd-row">
        <label className="pcd-field">
          <span className="pcd-label">Reason</span>
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <label className="pcd-field">
          <span className="pcd-label">Contact email</span>
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </label>
      </div>
    </>
  );

  const renderReview = () => (
    <>
      {losses.length ? (
        <div className="pcd-note pcd-note--warn">
          <FiAlertTriangle />
          <div>
            <strong>Some features will be removed</strong>
            <p>{plan.name} doesn't include everything you have on {current.name}.</p>
          </div>
        </div>
      ) : (
        <div className="pcd-note">
          <FiZap />
          <div>
            <strong>{isUpgrade ? "You're unlocking more capability" : 'Your plan is changing'}</strong>
            <p>
              {gains.length
                ? `${plan.name} adds ${gains.slice(0, 3).join(', ').toLowerCase()}${gains.length > 3 ? ' and more' : ''}.`
                : `${plan.name} keeps every feature you use today.`}
            </p>
          </div>
        </div>
      )}

      <p className="pcd-section">What changes</p>
      <div className="pcd-row pcd-row--top">
        <div className="pcd-box">
          <strong>Features you gain</strong>
          {gains.length ? (
            <ul>{gains.map((f) => <li key={f}><span className="pcd-plus"><FiPlus /></span>{f}</li>)}</ul>
          ) : <p className="pcd-muted">No new features.</p>}
        </div>
        <div className="pcd-box">
          <strong>Features you lose</strong>
          {losses.length ? (
            <ul>{losses.map((f) => <li key={f}><span className="pcd-minus"><FiMinus /></span>{f}</li>)}</ul>
          ) : <p className="pcd-muted">No features will be removed.</p>}
        </div>
      </div>

      <div className="pcd-box pcd-breakdown">
        <strong>Price breakdown</strong>
        {preview.effective === 'next_cycle' && (
          <p className="pcd-muted">
            Nothing is charged today. From {formatDate(preview.effectiveDate)} your invoice will be:
          </p>
        )}
        {preview.lines.map((l, i) => (
          <div key={i} className="pcd-line">
            <span>{l.description}</span>
            <span className={l.amount < 0 ? 'pcd-credit' : ''}>
              {l.amount < 0 ? '−' : ''}{formatMoney(Math.abs(l.amount), currency)}
            </span>
          </div>
        ))}
        <div className="pcd-line"><span>{preview.effective === 'next_cycle' ? 'Subtotal' : 'Due today'}</span><span>{formatMoney(preview.subtotal, currency)}</span></div>
        <div className="pcd-line"><span>GST</span><span>{formatMoney(preview.tax, currency)}</span></div>
        <div className="pcd-line pcd-line--total">
          <span>{preview.effective === 'next_cycle' ? `Total on ${formatDate(preview.effectiveDate)}` : 'Total due today'}</span>
          <span>{formatMoney(preview.effective === 'next_cycle' ? preview.total : dueToday, currency)}</span>
        </div>
      </div>
    </>
  );

  const renderConfirm = () => (
    <>
      <div className="pcd-done">
        <span><FiCheck /></span>
        <h3>Confirm your change</h3>
        <p className="pcd-muted">Review the final details before submitting.</p>
      </div>
      <dl className="pcd-summary">
        <div><dt>New plan</dt><dd>{plan.name}</dd></div>
        <div><dt>Billing cycle</dt><dd>{cycle === 'year' ? 'Annual' : 'Monthly'}</dd></div>
        <div><dt>Staff</dt><dd>{staff}</dd></div>
        <div><dt>Effective</dt><dd>{effective === 'next_cycle' ? formatDate(preview.effectiveDate) : 'Immediately'}</dd></div>
        <div className="pcd-summary-total">
          <dt>Total due today</dt>
          <dd>{formatMoney(dueToday, currency)}</dd>
        </div>
      </dl>

      <p className="pcd-section pcd-section--small">Payment method</p>
      {overview.paymentMethod ? (
        <label className="pcd-option pcd-option--wide on">
          <input type="radio" checked readOnly />
          <span className="pcd-card">
            <span className="pcd-brand">{overview.paymentMethod.brand}</span>
            <strong>{overview.paymentMethod.brand} •••• {overview.paymentMethod.last4}</strong>
          </span>
          <span className="pcd-default">Default</span>
        </label>
      ) : (
        <p className={needsCard ? 'pcd-error' : 'pcd-muted'}>
          No card on file.{needsCard ? ' Add one before paying.' : ''}
        </p>
      )}
      <button type="button" className="pcd-link" onClick={onManageCards}>
        {overview.paymentMethod ? 'Use a different card' : 'Add a card'}
      </button>
    </>
  );

  const nothingChanges =
    plan &&
    price &&
    price.id === current.priceId &&
    Math.max(0, staff - (included ?? staff)) === (current.extraStaff || 0);

  return createPortal(
    <div className="pcd-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <aside className="pcd-drawer" role="dialog" aria-modal="true" aria-labelledby="pcd-title">
        <header className="pcd-head">
          <div>
            <p className="pcd-eyebrow">Subscription change</p>
            <h2 id="pcd-title">Change your plan</h2>
          </div>
          <button type="button" className="pcd-close" aria-label="Close" disabled={busy} onClick={onClose}>
            <FiX />
          </button>
        </header>

        <ol className="pcd-steps">
          {STEPS.map((label, i) => (
            <li key={label} className={i < step ? 'done' : i === step ? 'on' : ''}>
              <span>{i < step ? <FiCheck /> : i + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        <div className="pcd-body">
          {!plans ? (
            <p className="pcd-muted">{error || 'Loading plans…'}</p>
          ) : step === 0 ? renderChoose() : step === 1 ? renderReview() : renderConfirm()}
          {error && plans ? <p className="pcd-error" role="alert">{error}</p> : null}
        </div>

        <footer className="pcd-foot">
          <div>
            <small>{['', 'Review changes', 'Ready to confirm'][step] || `${plan?.name || ''} plan`}</small>
            <strong>{plan?.isCustom ? 'Custom' : footerPrice}</strong>
          </div>
          <div className="pcd-actions">
            {step > 0 && (
              <button type="button" className="pcd-btn" disabled={busy} onClick={() => { setError(''); setStep(step - 1); }}>
                Back
              </button>
            )}
            {step === 0 && (
              plan?.isCustom ? (
                <button type="button" className="pcd-btn pcd-btn--primary" onClick={() => { onClose(); onContactSales(plan); }}>
                  Contact sales
                </button>
              ) : (
                <button type="button" className="pcd-btn pcd-btn--primary" disabled={busy || !price || nothingChanges} onClick={loadPreview}>
                  {busy ? 'Pricing…' : nothingChanges ? 'Current plan' : 'Continue'}
                </button>
              )
            )}
            {step === 1 && (
              <button type="button" className="pcd-btn pcd-btn--primary" onClick={() => setStep(2)}>Continue</button>
            )}
            {step === 2 && (
              <button type="button" className="pcd-btn pcd-btn--primary" disabled={busy || needsCard} onClick={confirm}>
                {busy ? 'Confirming…' : dueToday > 0 ? `Confirm and pay ${formatMoney(dueToday, currency)}` : 'Confirm change'}
              </button>
            )}
          </div>
        </footer>
      </aside>
    </div>,
    document.body,
  );
}
